import { useRef } from 'react';
import { format } from 'date-fns';
import { getDefaultActivityImage } from 'utils/shared/getDefaultActivityImage';
import { getProfileImageUrlOrPlaceholder } from 'utils/shared/user/getProfileImageUrlOrPlaceholder';
import { useEngagementImpression } from 'hooks/useEngagementImpression';
import License from 'svg/License';
import Location from 'svg/Location';
import ActivityBadge from 'components/ActivityBadge';
import Link from 'components/Link';
import { ProgramSummary } from './types';

const MAX_AVATARS = 5;

interface Props {
  program: ProgramSummary;
}

export default function ProgramCard({ program }: Props) {
  const cardRef = useRef<HTMLDivElement>(null);
  useEngagementImpression(cardRef, {
    entityType: program.entityType,
    entityId: program.id,
    source: 'CLUB_PAGE',
  });

  const startDate = new Date(program.startDateTime);
  const dateLabel = format(startDate, 'MMM d');
  const startTimeLabel = format(startDate, 'h:mm a').toLowerCase();
  const endTimeLabel = program.endDateTime
    ? format(new Date(program.endDateTime), 'h:mm a').toLowerCase()
    : null;

  const spotsLeft =
    program.participantLimit != null && program.participantCount != null
      ? Math.max(program.participantLimit - program.participantCount, 0)
      : null;

  return (
    <Link
      href={program.href}
      className="block w-full rounded-2xl bg-color-bg-lightmode-primary p-ds-lg shadow-[0px_4px_8px_rgba(0,0,0,0.08)] dark:bg-color-bg-darkmode-secondary"
    >
      <div className="flex items-center gap-ds-sm">
        <span className="typography-product-subheading text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
          {dateLabel}
        </span>
        <span className="h-1 w-1 shrink-0 rounded-full bg-color-brand-primary" />
        <span className="typography-product-body text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
          {startTimeLabel}
          {endTimeLabel ? ` - ${endTimeLabel}` : ''}
        </span>
      </div>
      <div ref={cardRef} className="mt-ds-md flex gap-ds-sm">
        <img
          src={
            program.imageUrl ||
            getDefaultActivityImage({ type: program.activityType, sport: program.sport })
          }
          alt={program.title}
          className="h-[7.5rem] w-[7.5rem] shrink-0 rounded-xl border border-color-border-input-lightmode object-cover dark:border-color-border-input-darkmode"
        />
        <div className="flex min-w-0 flex-1 flex-col justify-between">
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between gap-2">
              <ActivityBadge
                type={program.activityType}
                sport={program.sport}
                isRfx={program.isRfx}
                size="xs"
              />
              {!!program.priceDisplay && (
                <span className="typography-product-card-title-mobile shrink-0 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                  {program.priceDisplay}
                </span>
              )}
            </div>
            <p className="typography-product-card-title-mobile line-clamp-1 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
              {program.title}
            </p>
            {!!program.locationName && (
              <div className="flex items-center gap-1">
                <Location className="h-3.5 w-3.5 shrink-0 text-color-text-lightmode-icon dark:text-color-text-darkmode-icon" />
                <span className="truncate text-xs text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                  {program.locationName}
                </span>
              </div>
            )}
            {!!program.organizerName && (
              <div className="flex items-center gap-1">
                <License className="h-3.5 w-3.5 shrink-0 text-color-text-lightmode-icon dark:text-color-text-darkmode-icon" />
                <span className="truncate text-xs text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                  {program.organizerName}
                </span>
              </div>
            )}
          </div>
          {program.participantCount != null && (
            <div className="flex items-center justify-between gap-2 pt-ds-sm">
              <div className="flex items-center gap-1">
                <span className="text-xs text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                  {program.participantLimit != null
                    ? `${program.participantCount}/${program.participantLimit}`
                    : `${program.participantCount} ${
                        program.participantCount === 1 ? 'player' : 'players'
                      }`}
                </span>
                <div className="isolate flex">
                  {program.participantAvatarPaths.slice(0, MAX_AVATARS).map((path, index) => (
                    <img
                      key={index}
                      src={getProfileImageUrlOrPlaceholder({ path: path || '' })}
                      alt=""
                      className="relative h-5 w-5 rounded-full border border-color-bg-lightmode-primary object-cover dark:border-color-bg-darkmode-primary"
                      style={{ marginLeft: index > 0 ? '-0.375rem' : 0 }}
                    />
                  ))}
                </div>
              </div>
              {spotsLeft != null && (
                <span className="text-xs text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                  {spotsLeft} {spotsLeft === 1 ? 'spot' : 'spots'} left
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
